import * as React from 'react';
import { FC, useMemo } from 'react';

import { omit } from 'lodash-es';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';

import { BackArrowHistory } from 'components/navigation/back-arrow';
import useLocation from 'hooks/location/use-location';
import { useOrderTimeTimezone } from 'hooks/use-order-time-timezone';
import { useSupportData } from 'hooks/use-support-data';
import { StoreLocatorProvider } from 'pages/store-locator/hooks';
import { serializeServiceMode } from 'state/service-mode';
import { formatDateObject } from 'utils/form';

import {
  useGetSupportCategoryGroupQuery,
  useGetSupportSubcategoryQuery,
} from '../../../generated/sanity-graphql';
import useAuth from '../../../hooks/auth/use-auth';
import { useOrderDetails } from '../../../hooks/use-order-details';
import { useLocale } from '../../../state/intl';
import { REGIONS } from '../../../state/intl/types';
import { useKnowledgeForceCaseCreation } from '../../../state/knowledge-force/hooks/use-knowledge-force-case-creation';
import { ISupportFormData } from '../../../state/knowledge-force/types';
import { LaunchDarklyFlag, useFlag } from '../../../state/launchdarkly';
import { ErrorView } from '../error-view';
import { LoadingView } from '../loading-view';
import { InnerBox, PageWrap } from '../support-form-v2.styled';
import { invalidStatusesWhileEnroute } from '../types';

import { SupportV2FormView } from './support-v2-form-view';
import { ISupportV2LocationState } from './types';

/**
 * Displays the support form and thank-you/error messages after submission
 */
const SupportV2FormPage: FC = () => {
  const { location } = useLocation();
  const query = new URLSearchParams(location.search);
  const { language, region } = useLocale();
  const fromApp = query.get('fromApp') === 'true';

  // Add language param to URL if it doesn't exist and fromApp is true
  if (fromApp && !query.has('lang')) {
    query.set('lang', language);
    window.history.replaceState({}, '', `${location.pathname}?${query.toString()}`);
  }

  const orderId = query.get('orderId');
  const subcategoryId = query.get('subcategoryId');
  const categoryId = query.get('categoryId');
  // the following username and useremail will come from the Tims App only (in-app browswer)
  const username = query.get('username') ?? '';
  const useremail = query.get('useremail') ?? '';

  const { formatMessage } = useIntl();
  const { user } = useAuth();
  const useHighVolumeSupportMessage = useFlag(LaunchDarklyFlag.USE_HIGH_VOLUME_SUPPORT_MSG);

  const {
    caseReferenceNumber,
    createCaseState,
    createSupportCase,
    createCaseError,
  } = useKnowledgeForceCaseCreation();

  const { supportCategoryGroups, isSupportDataLoading } = useSupportData();
  const {
    data: supportSubcategoryData,
    loading: isSupportSubcategoryloading,
  } = useGetSupportSubcategoryQuery({
    variables: { id: subcategoryId! },
    skip: !subcategoryId,
  });
  const {
    data: supportSupportCategoryGroupData,
    loading: isSupportSupportCategoryGrouploading,
  } = useGetSupportCategoryGroupQuery({
    variables: { id: categoryId! },
    skip: !categoryId,
  });

  const { order, loading: isOrderLoading } = useOrderDetails(orderId);

  const refundReason = () => {
    if (order?.order?.refund) {
      return formatMessage({ id: 'supportFormOrderAlreadyRefunded' });
    } else if (order?.delivery && !invalidStatusesWhileEnroute.has(order?.delivery?.status)) {
      return formatMessage({ id: 'supportFormDeliveryActive' });
    }
    return formatMessage({ id: 'supportFormRefundGenericError' });
  };

  const { refundIneligibilityReason: alertMessage } = (location?.state ||
    {}) as ISupportV2LocationState;

  const supportCategory = supportSupportCategoryGroupData?.SupportCategoryGroup;
  const primaryCategoryName = supportCategory?.name?.locale ?? '';
  const showRestaurantSelector = supportCategory?.showRestaurantSelector;

  const supportSubcategory = supportSubcategoryData?.SupportSubcategory;
  const knowledgeForceIssueId = supportSubcategory?.knowledgeForceIssueId;
  const subcategoryName = supportSubcategory?.name?.locale ?? '';
  const showServiceModeSelector = supportSubcategory?.supportSubcategoryServiceModeSelector;
  const faqs =
    supportSubcategory?.supportSubcategoryFaqs?.map(faq => {
      return {
        title: faq?.supportFaqTitle?.locale ?? '',
        content: faq?.supportFaqContent?.localeRaw,
      };
    }) || [];

  const { orderTimeTimezone } = useOrderTimeTimezone(order);

  const initialValues: ISupportFormData = useMemo(
    () => ({
      comments: '',
      email: useremail !== '' ? useremail : user?.details?.email ?? '',
      issueId: knowledgeForceIssueId || '',
      name: username !== '' ? username : user?.details?.name ?? '',
      orderDate: order?.createdAt,
      agreesToTermsOfService: false,
      serviceMode: '',
      issueSubCategory: `${primaryCategoryName}: ${subcategoryName}`,
      ...(!!showRestaurantSelector && {
        storeId: '',
        storeAddress: '',
        orderDateTime: {
          month: '',
          day: '',
          year: '',
        },
      }),
    }),
    [
      useremail,
      username,
      user,
      knowledgeForceIssueId,
      order,
      primaryCategoryName,
      subcategoryName,
      showRestaurantSelector,
    ]
  );

  const handleSupportFormSubmit = (formData: ISupportFormData) => {
    const { agreesToTermsOfService, orderDateTime, ...sanitizedFormData } = formData;

    if (!agreesToTermsOfService) {
      return;
    }

    // orderDate is just for display, not for submitting to the server
    const sanitizedFormDataWithoutOrderDate = omit(sanitizedFormData, ['orderDate']);

    const userAccountRegion = user?.details?.isoCountryCode ?? null;

    const submissionData = {
      ...sanitizedFormDataWithoutOrderDate,
      ...(order?.rbiOrderId && { rbiOrderId: order.rbiOrderId }),
      ...(order?.cart?.serviceMode && {
        serviceMode: serializeServiceMode(order?.cart?.serviceMode),
      }),
      ...(showRestaurantSelector && { orderDateTime: formatDateObject(orderDateTime) }),
      userRegion: REGIONS[region],
      userAccountRegion,
      localStoreOrderTime: orderTimeTimezone,
    };

    createSupportCase(submissionData);
  };

  if (
    isSupportDataLoading ||
    isSupportSubcategoryloading ||
    isOrderLoading ||
    isSupportSupportCategoryGrouploading
  ) {
    return <LoadingView />;
  }

  if (!supportCategoryGroups || supportCategoryGroups.length === 0) {
    return <ErrorView message={formatMessage({ id: 'looksLikeWereExperiencingIssues' })} />;
  }

  return (
    <PageWrap>
      <InnerBox>
        <BackArrowHistory blockWebViewExitMessage={true} />
        <Helmet title={subcategoryName} />
        <StoreLocatorProvider>
          <SupportV2FormView
            alertMessage={isOrderLoading ? alertMessage : refundReason()}
            bigTitle={subcategoryName}
            smallTitle={primaryCategoryName}
            caseReferenceNumber={caseReferenceNumber}
            createCaseError={createCaseError}
            createCaseState={createCaseState}
            faqs={faqs}
            handleSupportFormSubmit={handleSupportFormSubmit}
            initialValues={initialValues}
            orderDate={order?.createdAt}
            serviceMode={serializeServiceMode(order?.cart?.serviceMode)}
            useHighVolumeSupportMessage={useHighVolumeSupportMessage}
            showRestaurantSelector={showRestaurantSelector}
            showServiceModeSelector={showServiceModeSelector}
            fromApp={fromApp}
          />
        </StoreLocatorProvider>
      </InnerBox>
    </PageWrap>
  );
};

export default SupportV2FormPage;
